import type {CmLogger} from "@cm/log"
import type {DataResolver} from "@cm/material-nodes"
import {deserializeMaterialGraph, getPhysicalSizeInfoForMaterialGraph} from "@cm/material-nodes"
import type {MaterialData} from "#material-outputs-filenames/types"

/** The tag type a material's range is carried on. Named by value: the enum is generated code this package must not depend on. */
const MATERIAL_RANGE_TAG_TYPE = "MaterialRange"

/** Whatever `deserializeMaterialGraph` accepts — taken from it rather than from `@cm/graph-v3`, which this package does not depend on. */
type SerializedMaterialGraph = Parameters<typeof deserializeMaterialGraph>[0]

/**
 * The material fields an asset's filename data is assembled from. Deliberately a structural shape rather
 * than either caller's row type, so the browser's GraphQL fragment and the backend's Prisma select
 * both satisfy it without either becoming the other's contract.
 */
export type MaterialFieldsForAssetData = {
    legacyId: number
    name?: string | null
    articleId?: string | null
    organization?: {name?: string | null} | null
    tagAssignments: {tag: {type: string; name: string; otherInfo?: string | null}}[]
    latestCyclesRevision?: {number: number; graphNode: SerializedMaterialGraph} | null
}

/** The half of `AssetData` that describes the material rather than the asset. */
export type PartialAssetData = {
    organizationName: string
    materialData: MaterialData
}

/**
 * A material cannot be named from the schema. Distinct from a programming error because the four
 * ways this happens are all data the user can fix, and callers have to report them as such rather
 * than as an internal failure.
 */
export class AssetNamingError extends Error {
    constructor(message: string) {
        super(message)
        this.name = "AssetNamingError"
    }
}

export function rangeInfoForMaterial(material: MaterialFieldsForAssetData): MaterialData["rangeInfo"] {
    const materialRanges = material.tagAssignments
        .filter(({tag}) => tag.type === MATERIAL_RANGE_TAG_TYPE)
        .map(({tag}) => ({name: tag.name, otherInfo: tag.otherInfo ?? null}))

    if (materialRanges.length > 1) throw new AssetNamingError(`At most one material range expected for material, found ${materialRanges.length}`)

    return materialRanges[0] ?? {name: null, otherInfo: null}
}

/**
 * Assembles the material half of an asset's filename data: everything `getFilenameForAssetData` needs
 * that is not a property of the asset itself.
 */
export async function partialAssetDataForMaterial(material: MaterialFieldsForAssetData, resolve: DataResolver, logger: CmLogger): Promise<PartialAssetData> {
    const organizationName = material.organization?.name
    if (!organizationName) throw new AssetNamingError(`Organization name missing`)

    if (!material.latestCyclesRevision) throw new AssetNamingError(`Material has no latest cycles revision`)

    const rangeInfo = rangeInfoForMaterial(material)

    const materialGraph = deserializeMaterialGraph(material.latestCyclesRevision.graphNode)
    const physicalSize = await getPhysicalSizeInfoForMaterialGraph(materialGraph, resolve, logger)
    if (!physicalSize) throw new AssetNamingError(`Could not determine physical size for material ${material.legacyId}`)

    return {
        organizationName: organizationName.toLowerCase(),
        materialData: {
            articleId: material.articleId ?? null,
            name: material.name ?? null,
            rangeInfo,
            widthCm: physicalSize.widthCm,
            heightCm: physicalSize.heightCm,
            revisionNumber: material.latestCyclesRevision.number,
        },
    }
}
